import { useAuth } from '@/contexts/AuthContext'
import { useState } from 'react'

import { Button } from '@/components/ui/button'

import { useFetchProfile, useUpdateProfile, useChangePassword } from '@/hooks/features/profile'
import { EditProfileDialog, ChangePasswordDialog } from '@/components/features/profile'
import { ProfileHeader, ProfileInfo, ProfileSecurity } from '@/components/features/profile/components'
import { Spinner } from '@/components/ui/spinner'

export const Profile = () => {
  const { logout } = useAuth()
  const { data: profile, isLoading, isError } = useFetchProfile()
  const updateProfileMutation = useUpdateProfile()
  const changePasswordMutation = useChangePassword()

  const [isEditOpen, setIsEditOpen] = useState(false)
  const [isPasswordOpen, setIsPasswordOpen] = useState(false)

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Spinner className="size-6 text-muted-foreground" />
          <p className="text-sm text-text-2">Loading your profile…</p>
        </div>
      </div>
    )
  }

  if (isError || !profile) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="max-w-md space-y-3 rounded-lg border border-border bg-card p-6 text-center shadow-z1">
          <h2 className="text-heading-md text-ink">Unable to load profile</h2>
          <p className="text-sm text-text-2">
            We encountered an error while fetching your account details. Please ensure you are logged in and try again.
          </p>
          <Button variant="secondary" onClick={() => window.location.reload()}>
            Try again
          </Button>
        </div>
      </div>
    )
  }

  const handleUpdateProfile = async (data: any) => {
    await updateProfileMutation.mutateAsync(data)
    setIsEditOpen(false)
  }

  const handleChangePassword = async (data: any) => {
    await changePasswordMutation.mutateAsync(data)
    setIsPasswordOpen(false)
  }

  return (
    <div className="mx-auto w-full max-w-[1088px]">
      <ProfileHeader onEdit={() => setIsEditOpen(true)} />

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_288px] lg:gap-6">
        <div>
          <ProfileInfo profile={profile} />
        </div>

        <div>
          <ProfileSecurity
            profile={profile}
            onChangePassword={() => setIsPasswordOpen(true)}
            onLogout={logout}
          />
        </div>
      </div>

      <EditProfileDialog
        open={isEditOpen}
        profile={profile}
        closeDialog={() => setIsEditOpen(false)}
        onUpdate={handleUpdateProfile}
        loading={updateProfileMutation.isPending}
      />

      <ChangePasswordDialog
        open={isPasswordOpen}
        closeDialog={() => setIsPasswordOpen(false)}
        onUpdate={handleChangePassword}
        loading={changePasswordMutation.isPending}
      />
    </div>
  )
}
